/**
 * Browser error tracking via Sentry.
 *
 * Reads the Sentry DSN from window.__APP_CONFIG__ (injected by the Express
 * server at page load). No-ops silently when the DSN is absent — which is the
 * case in desktop/mobile shells (no server) and until a Sentry project is
 * created for this app. Loaded from Sentry's CDN rather than bundled, matching
 * how PostHog and TF.js are loaded: bundling would break the dev importmap
 * setup (raw ES modules, no bundler).
 */

import { gameEventLog } from './logging.js';
import { isAnalyticsEnabled } from './platform.js';
import { reportIntegrityEvent } from './integrity.js';

/** Pinned CDN bundle; keep the major in step with @sentry/node in package.json. */
const SENTRY_CDN_URL = 'https://browser.sentry-cdn.com/10.63.0/bundle.min.js';
/**
 * SRI hash for SENTRY_CDN_URL — the browser refuses to run the bundle unless
 * its bytes match. Regenerate WITH the URL whenever the version above changes:
 *   curl -fsSL <SENTRY_CDN_URL> | openssl dgst -sha384 -binary | openssl base64 -A
 */
const SENTRY_CDN_SRI = 'sha384-DK4NLLOVDh6BGBXQ48eIAFQ6DET3Y3pPMh/1xZBluw9YlZC9d51bMNXIerBn9sQM';

let initialised = false;

/**
 * Bootstrap Sentry from its CDN bundle. Called once from main.js.
 * Safe to call multiple times; subsequent calls are no-ops.
 */
export function initErrorTracking() {
    const cfg = window.__APP_CONFIG__;
    const dsn = cfg?.SENTRY_DSN;

    // isAnalyticsEnabled() covers the stored opt-out as well as nolog mode: a
    // player who said "send nothing" did not mean "send nothing except when it
    // breaks".
    if (!isAnalyticsEnabled()) {
        gameEventLog('[sentry] disabled — nolog mode or player opted out');
        return;
    }
    if (!dsn || initialised) {
        gameEventLog('[sentry] skipping init —', !dsn ? 'no DSN' : 'already initialised');
        return;
    }
    initialised = true;

    const script = document.createElement('script');
    script.crossOrigin = 'anonymous';
    script.integrity = SENTRY_CDN_SRI;
    script.async = true;
    script.src = SENTRY_CDN_URL;
    script.onload = () => {
        window.Sentry.init({
            dsn,
            // Errors only — no client-side tracing; gameplay perf is tracked
            // by our own benchmarks, and tracing would bloat every session.
            tracesSampleRate: 0,
            // Errors thrown from inside the CrazyGames SDK's own code are theirs
            // to fix, not ours: its window 'error' listener dereferences
            // `event.error.name`, which Safari leaves null for cross-origin
            // script errors, so every such error produced a second, SDK-owned
            // TypeError here (LORD-OF-THE-SWARM-D). denyUrls only matches the
            // frame that threw, so our own errors with SDK frames deeper in
            // the stack still report.
            denyUrls: [/sdk\.crazygames\.com/],
        });
        gameEventLog('[sentry] ready');
    };
    // Fires for a blocked/offline load AND for an SRI mismatch — the browser
    // cannot tell us which from here. A one-off is almost always an ad blocker;
    // a persistent one after a deploy means the pinned hash needs regenerating.
    script.onerror = () => reportIntegrityEvent(
        'sentry',
        'CDN bundle failed to load — ad blocker/offline, or the pinned SRI no longer matches this URL.',
        { url: SENTRY_CDN_URL });
    document.head.appendChild(script);
}
